import { useCallback, useEffect, useMemo } from 'react';
import { OptionKey, useQuery, useQueryClient } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import _ from 'lodash';

import { api } from '@/api';
import { DEFAULT_DATE_FORMAT } from '@/common/constants';
import PATHS from '@/common/paths';
import { Pagination,PaginationParams } from '@/common/types';

export const QUERY_KEY = 'useActivitiesList';
const REFUND_TRANSACTION_TYPE = 'PAYMENT_CARD_RETURN';
const REFUND = 'REFUND';

export interface ActivityDTO {
  posting_id: number;
  original_fee_posting_id?: string;
  claim_id: number;
  authorization_id: number;
  category_name: string;
  status_date: string;
  memo: string;
  status: string;
  amount: number;
  transaction_type: string;
  has_active_hold: boolean;
}
export interface ActivityRow {
  claimId: number;
  postingId: number;
  originalFeePostingId?: string;
  amount: number;
  category: string;
  title: string;
  date: string;
  status: string;
  transactionType?: string;
  hasActiveHold?: boolean;
}

export interface CurrentPaginationParams extends PaginationParams {
  accountId: string;
  types?: OptionKey[];
  startDate?: Date | null;
  endDate?: Date | null;
  hasActiveHold?: boolean;
}
const useActivitiesListQuery = ({
  page,
  perPage,
  searchString,
  accountId,
  types,
  startDate,
  endDate,
  hasActiveHold,
}: CurrentPaginationParams) => {
  const { isLoading, isSuccess, isError, data, refetch, isFetched } = useQuery(
    [
      QUERY_KEY,
      page,
      perPage,
      searchString,
      accountId,
      types,
      startDate,
      endDate,
      hasActiveHold,
    ],
    () => api.get<Pagination<ActivityDTO>>(PATHS.ACCOUNT_ACTIVITIES_BY_ID(accountId), {
      params: {
        page,
        size: perPage,
        ...searchString ? { memo: searchString } : {},
        ...types?.length ? {
          transaction_types: types?.join(','),
        } : {},
        period_to: endDate,
        period_from: startDate,
        ...typeof hasActiveHold === 'boolean' ? { has_active_hold: hasActiveHold } : {},
      },
    }),
    {
      cacheTime: 0,
      enabled: Boolean(accountId),
    }
  );
  const queryClient = useQueryClient();
  useEffect(() => {
    if (accountId) {
      refetch();
    } else {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY],
      });
    }
  }, [
    page,
    perPage,
    searchString,
    accountId,
    types,
    startDate,
    endDate,
    refetch,
    queryClient,
  ]);

  const isRefund = useCallback((item: ActivityDTO) => item?.transaction_type === REFUND_TRANSACTION_TYPE, []);

  const formattedData = useMemo<ActivityRow[]>(() => data?.data?.content?.map((item) => ({
    claimId: item.claim_id,
    postingId: item.posting_id,
    originalFeePostingId: _.toString(item?.original_fee_posting_id),
    amount: item.amount,
    category: isRefund(item) ? REFUND : item.category_name,
    title: isRefund(item) ? `Return ${item.memo}` : item.memo,
    date: item.status_date ? dayjs(item.status_date).format(DEFAULT_DATE_FORMAT) : '',
    status: item.status,
    transactionType: item.transaction_type,
    hasActiveHold: item.has_active_hold,
  })) || [], [data, isRefund]);
  const total = useMemo(() => data?.data?.totalElements || 0, [data?.data?.totalElements]);

  return {
    data: formattedData,
    total,
    isError,
    isLoading,
    isSuccess,
    refetch,
    isFetched,
  };
};

export default useActivitiesListQuery;
